import React, { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  addInstance,
  addTemplate,
  clearPending,
  makeInstance,
  makeTemplateFromInstance,
  updateInstance,
} from '../../features/equipment/equipmentSlice';
import { setOutsideStockerExclusive } from '../../features/layout/setOutsideStockerExclusive';
import { isValidEquipmentFootprint } from '../../utils/equipmentGeometry';
import { openRightDrawerTab } from '../../features/ui/uiSlice';
import {
  CATEGORY_LABELS,
  EQUIPMENT_CATEGORIES,
  OBJECT_TYPES,
  OBJECT_TYPE_LABELS,
  DEFAULT_FOUP_CAPACITY,
  DEFAULT_PROCESS_WAIT_SEC,
  DEFAULT_STOCKER_PROCESS_WAIT_SEC,
  categoryToProcessType,
  processTypeToCategory,
} from '../../features/equipment/equipmentConstants';
import {
  formatInstanceLabel,
  nextInstanceSequence,
} from '../../features/equipment/equipmentNaming';
import { collectExistingEntityIds } from '../../utils/entityIds';
import type {
  EquipmentCategory,
  EquipmentInstance,
  EquipmentObjectType,
  EquipmentProcessType,
} from '../../features/equipment/equipmentTypes';
import ProcessTypeSelect from '../properties/ProcessTypeSelect';
import { useProjectProcessTypes } from '../../hooks/useProjectProcessTypes';

/**
 * Identify / edit modal. Opens when the drawing tool commits a footprint
 * (create) or when an existing instance is edited. When a template is chosen,
 * defaults are inherited automatically so the user only sets name and process.
 */
const EquipmentModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const pending = useAppSelector((s) => s.equipment.draw.pending);
  const editingId = useAppSelector((s) => s.equipment.draw.editingId);
  const editingInstance = useAppSelector((s) =>
    editingId ? s.equipment.instances[editingId] : null
  );
  const templates = useAppSelector((s) => s.equipment.templates);
  const templateOrder = useAppSelector((s) => s.equipment.templateOrder);
  const floorPlanName = useAppSelector((s) => s.floorplan.name);
  const instances = useAppSelector((s) => s.equipment.instances);
  const layoutElements = useAppSelector((s) => s.layout.elements);
  const { processTypes, addProcessType } = useProjectProcessTypes([
    ...Object.values(instances).map((instance) => instance.equipmentType),
    ...Object.values(instances).flatMap((instance) => instance.processTypes ?? []),
  ]);
  const sharedExistingIds = useMemo(
    () => collectExistingEntityIds(Object.values(layoutElements), Object.values(instances)),
    [layoutElements, instances]
  );
  const nextEquipmentLabel = formatInstanceLabel(
    'equipment',
    nextInstanceSequence(instances, 'equipment', sharedExistingIds)
  );
  const nextStockerLabel = formatInstanceLabel(
    'stocker',
    nextInstanceSequence(instances, 'stocker', sharedExistingIds)
  );

  const [templateId, setTemplateId] = useState<string>('');
  const [objectType, setObjectType] = useState<EquipmentObjectType>('equipment');
  const [category, setCategory] = useState<EquipmentCategory>('Photolithography');
  const [equipmentType, setEquipmentType] = useState<EquipmentProcessType>('PHOTO');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [foupCapacity, setFoupCapacity] = useState(DEFAULT_FOUP_CAPACITY);
  const [outsideStocker, setOutsideStocker] = useState(false);
  const [processWaitSec, setProcessWaitSec] = useState(DEFAULT_PROCESS_WAIT_SEC);

  const isEdit = !!editingId && !!editingInstance;

  const orderedTemplates = useMemo(
    () => templateOrder.map((id) => templates[id]).filter(Boolean),
    [templateOrder, templates]
  );

  // Reset the form during render whenever a new footprint / edit target appears.
  // (React's recommended pattern for deriving form state from changing props,
  // guarded so it only runs when the pending identity actually changes.)
  const [formFor, setFormFor] = useState<unknown>(null);
  if (pending && formFor !== pending) {
    setFormFor(pending);
    if (isEdit && editingInstance) {
      setTemplateId(editingInstance.templateId ?? '');
      setObjectType(editingInstance.objectType);
      setCategory(editingInstance.category);
      setEquipmentType(
        editingInstance.equipmentType ?? categoryToProcessType(editingInstance.category)
      );
      setName(editingInstance.name);
      setDescription(editingInstance.description);
      setTags(editingInstance.tags.join(', '));
      setFoupCapacity(editingInstance.foupCapacity ?? DEFAULT_FOUP_CAPACITY);
      setOutsideStocker(Boolean(editingInstance.outsideStocker));
      setProcessWaitSec(editingInstance.processWaitSec ?? DEFAULT_PROCESS_WAIT_SEC);
    } else {
      setTemplateId('');
      setObjectType('equipment');
      setCategory('Photolithography');
      setEquipmentType('PHOTO');
      setName(nextEquipmentLabel);
      setDescription('');
      setTags('');
      setFoupCapacity(DEFAULT_FOUP_CAPACITY);
      setOutsideStocker(false);
      setProcessWaitSec(DEFAULT_PROCESS_WAIT_SEC);
    }
  }

  if (!pending || (!isEdit && !isValidEquipmentFootprint(pending.polygon))) return null;

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const t = templates[id];
    if (!t) return;
    setObjectType(t.objectType);
    setCategory(t.category);
    setEquipmentType(categoryToProcessType(t.category));
    if (!name || name.startsWith('Equipment ') || /^EQ\d+$/i.test(name) || /^ST\d+$/i.test(name)) {
      setName(
        t.objectType === 'stocker'
          ? formatInstanceLabel('stocker', nextInstanceSequence(instances, 'stocker'))
          : formatInstanceLabel('equipment', nextInstanceSequence(instances, 'equipment'))
      );
    }
  };

  const parseTags = () =>
    tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

  const buildInstance = (): EquipmentInstance => {
    return makeInstance({
      polygon: pending.polygon,
      rotation: pending.rotation ?? 0,
      name: name.trim() || (objectType === 'stocker' ? nextStockerLabel : nextEquipmentLabel),
      objectType,
      category,
      equipmentType,
      description: description.trim(),
      tags: parseTags(),
      templateId: templateId || null,
      foupCapacity: objectType === 'stocker' ? foupCapacity : undefined,
      outsideStocker: objectType === 'stocker' ? outsideStocker : undefined,
      processWaitSec:
        objectType === 'stocker'
          ? undefined
          : objectType === 'equipment'
            ? processWaitSec
            : undefined,
      existingIds: sharedExistingIds,
    });
  };

  const handleSave = () => {
    if (!isEdit && pending && !isValidEquipmentFootprint(pending.polygon)) {
      dispatch(clearPending());
      return;
    }
    if (isEdit && editingInstance) {
      dispatch(
        updateInstance({
          id: editingInstance.id,
          changes: {
            templateId: templateId || null,
            objectType,
            category,
            equipmentType,
            name: name.trim() || editingInstance.name,
            description: description.trim(),
            tags: parseTags(),
            foupCapacity: objectType === 'stocker' ? foupCapacity : undefined,
            processWaitSec:
              objectType === 'stocker'
                ? DEFAULT_STOCKER_PROCESS_WAIT_SEC
                : objectType === 'equipment'
                  ? processWaitSec
                  : undefined,
          },
        })
      );
      if (objectType === 'stocker') {
        dispatch(
          setOutsideStockerExclusive({ kind: 'equipment', id: editingInstance.id }, outsideStocker)
        );
      }
    } else {
      const instance = buildInstance();
      dispatch(addInstance({ ...instance, outsideStocker: false }));
      if (objectType === 'stocker') {
        dispatch((d, getState) => {
          const order = getState().equipment.instanceOrder;
          const id = order[order.length - 1];
          if (id) d(setOutsideStockerExclusive({ kind: 'equipment', id }, outsideStocker));
        });
      }
    }
    dispatch(clearPending());
  };

  const handleSaveAsTemplate = () => {
    // Materialise the instance, derive a template from it, then link them.
    const instance = isEdit && editingInstance ? editingInstance : buildInstance();
    const tplName = window.prompt('Template name', name.trim() || 'New Template');
    if (!tplName) return;
    const template = makeTemplateFromInstance(instance, templates, tplName);

    if (isEdit && editingInstance) {
      dispatch(addTemplate(template));
      dispatch(
        updateInstance({
          id: editingInstance.id,
          changes: {
            templateId: template.id,
            objectType,
            category,
            equipmentType,
            name: name.trim() || editingInstance.name,
            description: description.trim(),
            tags: parseTags(),
            foupCapacity: objectType === 'stocker' ? foupCapacity : undefined,
            processWaitSec:
              objectType === 'stocker'
                ? DEFAULT_STOCKER_PROCESS_WAIT_SEC
                : objectType === 'equipment'
                  ? processWaitSec
                  : undefined,
          },
        })
      );
      if (objectType === 'stocker') {
        dispatch(
          setOutsideStockerExclusive({ kind: 'equipment', id: editingInstance.id }, outsideStocker)
        );
      }
    } else {
      const created = { ...instance, templateId: template.id, outsideStocker: false };
      dispatch(addTemplate(template));
      dispatch(addInstance(created));
      if (objectType === 'stocker') {
        dispatch((d, getState) => {
          const order = getState().equipment.instanceOrder;
          const id = order[order.length - 1];
          if (id) d(setOutsideStockerExclusive({ kind: 'equipment', id }, outsideStocker));
        });
      }
    }
    dispatch(clearPending());
    dispatch(openRightDrawerTab('equipment'));
  };

  const handleCancel = () => dispatch(clearPending());

  const labelClass = 'block text-xs font-semibold text-gray-600 mb-1';
  const inputClass =
    'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 backdrop-blur-sm px-4"
      onClick={handleCancel}
    >
      <div
        className="w-full max-w-lg max-h-[88vh] overflow-hidden rounded-xl bg-white shadow-2xl border border-gray-200 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 py-4 border-b border-gray-200">
          <div className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Equipment' : 'Identify Equipment'}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Semantic object on top of {floorPlanName ?? 'the floor plan'} — the DXF is not modified.
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          {orderedTemplates.length > 0 && (
            <div>
              <label className={labelClass}>Template (optional — auto-fills defaults)</label>
              <select
                value={templateId}
                onChange={(e) => applyTemplate(e.target.value)}
                className={inputClass}
              >
                <option value="">No template (custom object)</option>
                {orderedTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.icon} {t.name} · {OBJECT_TYPE_LABELS[t.objectType]}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Object Type</label>
              <select
                value={objectType}
                onChange={(e) => setObjectType(e.target.value as EquipmentObjectType)}
                className={inputClass}
              >
                {OBJECT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {OBJECT_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            {objectType === 'equipment' || objectType === 'stocker' ? (
              <div>
                <label className={labelClass}>
                  {objectType === 'stocker' ? 'Process Type' : 'Equipment Type'}
                </label>
                <ProcessTypeSelect
                  value={equipmentType}
                  options={processTypes}
                  className={inputClass}
                  onAddCustom={addProcessType}
                  onChange={(next) => {
                    if (!next) return;
                    setEquipmentType(next);
                    setCategory(processTypeToCategory(next));
                  }}
                />
              </div>
            ) : (
              <div>
                <label className={labelClass}>Process</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as EquipmentCategory)}
                  className={inputClass}
                >
                  {EQUIPMENT_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              placeholder="e.g. Litho Tool A3"
              autoFocus
            />
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`${inputClass} resize-none`}
              rows={2}
            />
          </div>

          {objectType === 'equipment' && (
            <div>
              <label className={labelClass}>Process Time (s)</label>
              <input
                type="number"
                min={0}
                step={1}
                value={processWaitSec}
                onChange={(e) => setProcessWaitSec(Number(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
          )}

          {objectType === 'stocker' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>FOUP Capacity</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={foupCapacity}
                  onChange={(e) => setFoupCapacity(Number(e.target.value) || 0)}
                  className={inputClass}
                />
              </div>
              <label className="flex items-end gap-2 pb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={outsideStocker}
                  onChange={(e) => setOutsideStocker(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Outside Stocker</span>
              </label>
              <p className="col-span-2 text-[10px] text-gray-500 -mt-1">
                Only one outside stocker is allowed across the layout.
              </p>
            </div>
          )}

          <div>
            <label className={labelClass}>Tags (comma-separated)</label>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className={inputClass}
              placeholder="critical, bay-2"
            />
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handleSaveAsTemplate}
            className="px-3 py-1.5 rounded border border-blue-300 text-sm font-medium text-blue-700 hover:bg-blue-50"
          >
            Save as Template
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 rounded border border-gray-300 text-sm text-gray-700 hover:bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-1.5 rounded text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentModal;
